"use client";

import React from "react";
import { motion } from "framer-motion";
import { Award, Briefcase, Star, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const specialists = [{
  id: 1,
  name: "Marcus Johnson",
  role: "Plumbing Specialist",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/specialist_marcus.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/specialist_marcus.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/specialist_marcus.png-wvc-sizes",
  specialty: "Pipes & Fixtures",
  focus: "Residential Plumbing",
  verified: true
}, {
  id: 2,
  name: "Sarah Davis",
  role: "HVAC Specialist",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/specialist_sarah.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/specialist_sarah.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/specialist_sarah.webp-wvc-sizes",
  specialty: "Cooling & Heating",
  focus: "System Tune-ups",
  verified: true
}, {
  id: 3,
  name: "David Chen",
  role: "Electrical Specialist",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/specialist_david.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/specialist_david.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/specialist_david.png-wvc-sizes",
  specialty: "Panels & Wiring",
  focus: "Home Upgrades",
  verified: true
}, {
  id: 4,
  name: "Michael Rodriguez",
  role: "Appliance Specialist",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/specialist_michael.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/specialist_michael.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/specialist_michael.png-wvc-sizes",
  specialty: "Major Appliances",
  focus: "Diagnostics & Repair",
  verified: true
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function Specialists() {
  return <section id="specialistteamprofiles" className="bg-background py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-12 text-center md:mb-16">
          <motion.h2 initial={{
          opacity: 0,
          y: -20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Meet Our Home Maintenance Specialists
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: -20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
            Connect with independent local pros across Atlanta who specialize in plumbing, HVAC, electrical, and appliance care.
          </motion.p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {specialists.map((specialist, i) => <motion.div key={specialist.id} variants={itemVariants} data-index={i}>
              <Card className="flex h-full flex-col overflow-hidden border-border/50 shadow-sm transition-shadow hover:shadow-md">
                <AspectRatio ratio={4 / 5} className="bg-muted">
                  <img src={specialist.image} data-wvc-srcset={specialist.imageSrcset} data-wvc-sizes={specialist.imageSizes} alt={`Portrait of ${specialist.name}, ${specialist.role}`} className="size-full object-cover" loading="lazy" />
                </AspectRatio>
                <CardContent className="flex flex-grow flex-col p-5">
                  <div className="mb-1 flex items-start justify-between gap-2">
                    <h3 className="font-default text-lg font-semibold text-foreground line-clamp-1">
                      {specialist.name}
                    </h3>
                    {specialist.verified && <CheckCircle2 className="mt-1 size-4 shrink-0 text-primary" aria-label="Verified Professional" />}
                  </div>
                  <p className="mb-4 text-sm font-medium text-primary">
                    {specialist.role}
                  </p>
                  
                  <div className="mb-4 flex flex-wrap gap-2">
                    <Badge variant="secondary" className="bg-secondary/10 text-secondary-foreground hover:bg-secondary/20">
                      <Award className="mr-1 size-3" />
                      {specialist.specialty}
                    </Badge>
                  </div>

                  <div className="mt-auto">
                    <Separator className="mb-4" />
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <Briefcase className="size-4 text-primary" />
                        <span className="font-medium text-foreground">{specialist.focus}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="mt-12 flex justify-center md:mt-16">
          <Button asChild size="lg" className="px-8">
            <Link to="#bookingrequest">
              View All Professionals
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}